import { graphql } from 'react-relay';

import { usePageContentId } from '@confluence/page-context';
import { useGetPageMode } from '@confluence/page-utils/entry-points/useGetPageMode.ts';
import { PageMode } from '@confluence/page-utils/entry-points/enums.ts';
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';

import type {
	useAllowNestedCommentsDetailsPanelContentCreatedDateQuery as useAllowNestedCommentsDetailsPanelContentCreatedDateQuery$data,
	useAllowNestedCommentsDetailsPanelContentCreatedDateQueryVariables as useAllowNestedCommentsDetailsPanelContentCreatedDateQuery$variables,
} from './__types__/useAllowNestedCommentsDetailsPanelContentCreatedDateQuery.ts';
const useAllowNestedCommentsDetailsPanelContentCreatedDateQuery = graphql`
	query useAllowNestedCommentsDetailsPanelContentCreatedDateQuery(
		$contentId: ID!
		$status: String!
	) {
		content(id: $contentId, status: [$status]) {
			nodes {
				id
				metadata {
					createdDate
				}
			}
		}
	}
`;
const NESTED_COMMENTS_LAUNCH_DATE = new Date(Date.UTC(2025, 2, 6)); // 6th March 2025. Only month is 0 indexed.

// NOTE: This hook is ONLY used for the legacy footer (i.e. that huge, old, js package)
// The new footer relies on state within useCommentsPanel
// This code should be deprecated alongside the legacy footer
export const useAllowNestedComments = (): boolean => {
	const [contentId] = usePageContentId();
	const pageMode = useGetPageMode();

	const {
		data: createdDateData,
		loading,
		error,
	} = useQueryInterop<
		useAllowNestedCommentsDetailsPanelContentCreatedDateQuery$data,
		useAllowNestedCommentsDetailsPanelContentCreatedDateQuery$variables
	>(useAllowNestedCommentsDetailsPanelContentCreatedDateQuery, {
		variables: {
			contentId: contentId ?? '',
			status: 'current',
		},
		skip: !contentId,
	});
	const pageCreatedDate = createdDateData?.content?.nodes?.[0]?.metadata?.createdDate;
	const isLivePage = pageMode === PageMode.LIVE;

	if (loading || error || !pageCreatedDate) {
		return false;
	}

	const pageCreateDateObject = new Date(pageCreatedDate);

	const isBeforeLaunchDate = pageCreateDateObject < NESTED_COMMENTS_LAUNCH_DATE;

	return isBeforeLaunchDate && !isLivePage;
};
